import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { cutToast } from 'utility/toast';
import { TripPatch, TripUpdateInput } from 'common/types/globalTypes';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { TravelAuthorization_travelAuthorization } from 'travelAuthorization/TravelPlan/view/__generated__/TravelAuthorization';
import {
  isFilteredTripListVar,
  setTripInitiateFlow,
  setUserDefaults,
  travelerDialogVisibleVar,
} from 'utility/cache/ui';
import { setTripsModalState } from '..';
import {
  TravelerAuthorizationCommonData,
  TravelerAuthorizationCommonData_tripSections_nodes,
} from '../../__generated__/TravelerAuthorizationCommonData';
import { FormView } from './FormView';
import { Trip, TripVariables } from './__generated__/Trip';
import { TripCreate, TripCreateVariables } from './__generated__/TripCreate';
import { TripUpdate, TripUpdateVariables } from './__generated__/TripUpdate';
import { TripCreateFormValues } from './types';
import {
  getDefaultValues,
  getDeletedTripItems,
  getNewTripDistributions,
  getNewTripItems,
  getRemovedTripDistributions,
  getSections,
  getTripPatch,
  getTripVariables,
  getUpdateTripItems,
  getUpdatedTripDistributions,
  sortMapDescendingBySortOrder,
} from './utils';
import { validationSchema } from './validations';
import TRIP_CREATE from './TripCreate.graphql';
import TRIP_UPDATE from './TripUpdate.graphql';
import TRIP from './Trip.graphql';
import COMMON_DATA from '../../TravelerAuthorizationCommonData.graphql';
import TRAVEL_AUTHORIZATION from '../../../TravelAuthorization.graphql';
import { TripBreakDownListModal } from './TripBreakDownListModal';

export type SectionProps = Omit<
  TravelerAuthorizationCommonData_tripSections_nodes,
  'tripSectionItems'
>;

interface TripModalProps {
  onSave?: () => void;
  onClose?: () => void;
  inputsDisabled: boolean;
  travelAuthorizationData:
    | Partial<TravelAuthorization_travelAuthorization>
    | null
    | undefined;
  onTripUpdate?: (data: TripUpdate | null | undefined) => void;
  businessUnitId: string | null;
  isOpen: boolean;
}
export const TripsModal: React.FC<TripModalProps> = ({
  travelAuthorizationData,
  inputsDisabled,
  onSave,
  onTripUpdate,
  businessUnitId,
  isOpen,
  onClose,
}) => {
  const userDefaults = useReactiveVar(setUserDefaults);
  const [layoutAdded, setLayoutAdded] = useState<string | null>(null);

  const tripsModalState = useReactiveVar(setTripsModalState);
  const [tripItemsState, setTripItemsState] = useState<
    Map<string, SectionProps>
  >(new Map());
  const { data: commonData } = useQuery<TravelerAuthorizationCommonData>(
    COMMON_DATA,
    { nextFetchPolicy: 'cache-only' }
  );
  const [
    createTrip,
    { data: tripCreateResponseData, loading: createTripLoading },
  ] = useMutation<TripCreate, TripCreateVariables>(TRIP_CREATE, {
    fetchPolicy: 'network-only',
  });

  const [updateTrip, { loading: updateTripLoading }] = useMutation<
    TripUpdate,
    TripUpdateVariables
  >(TRIP_UPDATE, {
    errorPolicy: 'all',
  });

  const {
    data: tripData,
    loading: loadingTrip,
    refetch,
  } = useQuery<Trip, TripVariables>(TRIP, {
    variables: { id: tripsModalState?.trip?.id! },
    skip: tripsModalState.isNew,
  });

  const formMethods = useForm<TripCreateFormValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });

  const { reset, handleSubmit, trigger } = { ...formMethods };

  useEffect(() => {
    if (!tripsModalState.isNew) {
      setTripInitiateFlow('ready');
    }
  }, [tripsModalState.isNew]);

  const onHandleSubmit = async (
    values: TripCreateFormValues,
    closeAfterComplete: boolean,
    addMore: boolean
  ) => {
    const isEdit = !tripsModalState.isNew;
    if (isEdit) {
      const defaultValues = getDefaultValues(
        tripData,
        commonData,
        userDefaults
      );
      const { trip, tripItems } = { ...defaultValues } as TripCreateFormValues;
      const tripPatch: TripPatch = getTripPatch(trip, values.trip);

      const newTripItems = getNewTripItems(values.tripItems);
      const updatedTripItems = getUpdateTripItems(tripItems, values.tripItems);
      const { deletedTripItems, deletedData } = getDeletedTripItems(
        tripItems,
        values.tripItems
      );

      const tripDistributionCreate = getNewTripDistributions(
        values,
        userDefaults
      );
      const tripDistributionDelete = getRemovedTripDistributions(
        tripData,
        values,
        deletedData
      );

      const tripDistributionUpdate = getUpdatedTripDistributions(
        values,
        defaultValues,
        deletedTripItems
      );

      const { errors, data } = await updateTrip({
        variables: {
          input: {
            tripId: tripsModalState?.trip?.id!,
            rowTimestamp: tripData?.trip?._rowTimestamp!,
            tripPatch: tripPatch,
            tripItemCreate: newTripItems,
            tripItemUpdate: updatedTripItems ? updatedTripItems : undefined,
            tripItemDelete: deletedTripItems,
            tripDistributionDelete,
            tripDistributionCreate,
            tripDistributionUpdate,
          } as TripUpdateInput,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: TRAVEL_AUTHORIZATION,
            variables: {
              id: travelAuthorizationData?.id,
            },
          },
        ],
      });

      if (errors?.length) {
        cutToast.error(errors[0].message);
      } else {
        cutToast.success('Trip item updated successfully.');
        onTripUpdate?.(data);
        setTripsModalState({
          ...tripsModalState,
          isModalOpen: !closeAfterComplete,
        });
        onSave?.();
      }
    } else {
      const variables = getTripVariables(
        values,
        travelAuthorizationData,
        userDefaults
      );

      const { errors, data } = await createTrip({
        variables,
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: TRAVEL_AUTHORIZATION,
            variables: {
              id: travelAuthorizationData?.id,
            },
          },
        ],

        update: (cache, { data }) => {
          try {
            cache.modify({
              fields: {
                travelAuthorizationSearchFilterTotals(existing) {
                  if (existing?.totalCount1 !== 0 && data?.tripCreate?.trip) {
                    const controlTotalAmount =
                      Number(existing?.totalAmount1) +
                      Number(data?.tripCreate?.trip?.controlTotalAmount);
                    return {
                      ...existing,
                      totalAmount1: controlTotalAmount.toString(),
                      totalCount1: existing.totalCount1
                        ? existing.totalCount1 + 1
                        : 1,
                    };
                  } else {
                    return {
                      ...existing,
                    };
                  }
                },
              },
            });
          } catch (error) {}
        },
      });

      if (!errors) {
        cutToast.success('Trip Created successfully');
        setTripsModalState({
          isModalOpen: !closeAfterComplete,
          isNew: addMore,
          trip: addMore ? undefined : data?.tripCreate?.trip!,
        });
        if (addMore) {
          setTripItemsState(new Map());
          const defaultValues = getDefaultValues(
            undefined,
            commonData,
            userDefaults
          );
          reset({
            ...defaultValues,
          } as TripCreateFormValues);
          trigger();
        }
      } else {
        cutToast.error(errors[0].message);
      }
    }
  };

  useEffect(() => {
    if (tripCreateResponseData) {
      const { _tripSectionLayout } = {
        ...tripCreateResponseData?.tripCreate?.trip?.tripItems.nodes[0],
      };
      if (_tripSectionLayout) {
        setLayoutAdded(_tripSectionLayout);
      }
    }
  }, [tripCreateResponseData]);

  const setDefault = useCallback(() => {
    if (tripData) {
      const defaultValues = getDefaultValues(
        tripData,
        commonData,
        userDefaults
      );
      const { map, initialLayout } = getSections(
        tripData,
        commonData,
        layoutAdded
      );

      if (!tripsModalState.isNew) {
        setLayoutAdded(initialLayout);
        const sortedMap = sortMapDescendingBySortOrder(map);
        if (sortedMap) setTripItemsState(sortedMap);
      }

      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonData, reset, tripData, tripsModalState]);

  useEffect(() => {
    setDefault();
    if (tripsModalState.isNew) {
      trigger();
    }
  }, [setDefault, trigger, tripData, tripsModalState.isNew]);

  const isLoading = createTripLoading || updateTripLoading;
  const isFilteredTripListOpen = useReactiveVar(isFilteredTripListVar);

  return (
    <FormProvider {...formMethods}>
      {!isFilteredTripListOpen && (
        <FormView
          isOpen={isOpen}
          refetch={refetch}
          onSave={(closeAfterComplete, addMore) => {
            handleSubmit((values) => {
              onHandleSubmit(values, closeAfterComplete, addMore);
            })();
          }}
          tripItemsState={tripItemsState}
          onTripItemsUpdate={(mapData, layoutAdded, adding) => {
            if (adding) {
              setTripItemsState(() => {
                const newMap = new Map(Array.from(mapData));
                return newMap;
              });
              setLayoutAdded(layoutAdded);
            }
          }}
          businessUnitId={businessUnitId}
          inputsDisabled={inputsDisabled}
          travelAuthorizationData={travelAuthorizationData}
          layoutAdded={layoutAdded}
          commonData={commonData}
          tripData={tripData?.trip}
          loading={isLoading}
          createTripLoading={createTripLoading}
          updateTripLoading={updateTripLoading}
          loadingTrip={loadingTrip}
          onClose={onClose}
        />
      )}
      {isFilteredTripListOpen && <TripBreakDownListModal />}
    </FormProvider>
  );
};
